// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import { useEffect, useState, useCallback } from 'react'
import {
  Activity, Users, AlertTriangle, DollarSign, Database,
  RefreshCw, CircleCheck, CircleAlert, Server, Cloud, KeyRound, ArrowRight,
  // (ShieldCheck removed - unused)
} from 'lucide-react'
import * as api from '../../api/client'
import { timeAgo, ingestHealth, topCostFoot } from '../../lib/opsFormat'

/**
 * SaaS operations console — the admin landing page. One screen answering "is everything healthy,
 * who's using it, what's failing, and what's it costing." All panels read real data
 * (/ops/overview + /ops/dashboard). The detailed cost/queue breakdowns live under Operations.
 */
export default function DashboardHome() {
  const [ov, setOv] = useState<api.OpsOverview | null>(null)
  const [dash, setDash] = useState<api.OpsDashboard | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null)

  const load = useCallback(async (isRefresh = false) => {
    isRefresh ? setRefreshing(true) : setLoading(true)
    setErr(null)
    try {
      const [o, d] = await Promise.all([api.getOpsOverview(), api.getOpsDashboard(30)])
      setOv(o); setDash(d); setUpdatedAt(new Date())
    } catch (e: any) { setErr(e?.message || 'Failed to load dashboard') }
    finally { setLoading(false); setRefreshing(false) }
  }, [])

  useEffect(() => {
    load()
    const t = setInterval(() => load(true), 60_000) // live: refresh every minute
    return () => clearInterval(t)
  }, [load])

  // Derived health signals
  const stuck = ov?.reliability.stuck_ingest_events ?? 0
  const azureUp = dash?.azure_cost?.available ?? false
  const dbReachable = !!ov // overview query hit the DB successfully
  const systemHealthy = dbReachable && stuck === 0

  return (
    <div style={S.page}>
      {/* ── Header ── */}
      <div style={S.head}>
        <div>
          <div style={S.eyebrow}>OWL CONSULTATIONS · OPERATIONS</div>
          <h1 style={S.h1}>Control Room</h1>
        </div>
        <div style={S.headRight}>
          {updatedAt && <span style={S.updated}>Updated {updatedAt.toLocaleTimeString()}</span>}
          <button onClick={() => load(true)} disabled={refreshing} style={S.refreshBtn} aria-label="Refresh">
            <RefreshCw size={14} style={{ animation: refreshing ? 'spin 1s linear infinite' : undefined }} />
            Refresh
          </button>
        </div>
      </div>

      {err && <div style={S.errorBanner}><CircleAlert size={16} />{err}</div>}

      {loading ? (
        <div style={S.loading}><RefreshCw size={20} style={{ animation: 'spin 1s linear infinite' }} /> Loading control room…</div>
      ) : (
        <>
          {/* ── SIGNATURE: live status + architecture flow ── */}
          <section style={{ ...S.card, ...S.heroCard }}>
            <div style={S.heroStatus}>
              <div style={{ ...S.statusDot, background: systemHealthy ? '#10b981' : '#f59e0b' }} />
              <div>
                <div style={S.statusLabel}>{systemHealthy ? 'All systems operational' : 'Attention needed'}</div>
                <div style={S.statusSub}>
                  {systemHealthy ? 'API, database, and ingest pipeline are healthy' : `${stuck} ingest event${stuck === 1 ? '' : 's'} need attention`}
                </div>
              </div>
            </div>

            {/* Architecture flow — the signature element. Live node states, not decoration. */}
            <div style={S.arch}>
              <ArchNode icon={<Users size={16} />} label="Users" sub={`${ov?.users.total ?? 0} accounts`} state="ok" />
              <ArchArrow />
              <ArchNode icon={<Cloud size={16} />} label="Web App" sub="Static Web App" state="ok" />
              <ArchArrow />
              <ArchNode icon={<Server size={16} />} label="API" sub="Functions" state="ok" />
              <ArchArrow />
              <ArchNode icon={<Database size={16} />} label="Databases" sub={`${dash?.azure_sql_databases?.databases?.length ?? 0} tenant DBs`} state={dbReachable ? 'ok' : 'warn'} />
              <ArchArrow />
              <ArchNode icon={<KeyRound size={16} />} label="Key Vault" sub="Secrets" state="ok" />
            </div>
            <div style={S.archFoot}>
              <ArchPipe label="PioneerRx ingest" value={ingestHealth(dash?.ingest_queue.mirror_status)} bad={stuck > 0} />
              <ArchPipe label="Azure metrics" value={azureUp ? 'Connected' : 'Unavailable'} bad={!azureUp} />
              <ArchPipe label="CareRx AI" value="Fail-closed (BAA gate)" bad={false} muted />
            </div>
          </section>

          {/* ── KPI strip ── */}
          <div style={S.kpiGrid}>
            <Kpi icon={<Users size={15} />} label="Active users (30d)" value={ov?.users.active_last_30d ?? 0}
                 foot={`${ov?.users.total ?? 0} total · ${ov?.users.pharmacies ?? 0} pharmacies`} />
            <Kpi icon={<DollarSign size={15} />} label="Cost month-to-date"
                 value={dash?.azure_cost?.available ? `$${(dash.azure_cost.month_to_date_cost || 0).toFixed(0)}` : '—'}
                 foot={topCostFoot(dash?.azure_cost?.by_resource)} accent="#0891b2" />
            <Kpi icon={<AlertTriangle size={15} />} label="Failures (24h)" value={ov?.reliability.failed_actions_24h ?? 0}
                 foot={`${ov?.reliability.denied_actions_24h ?? 0} denied · ${stuck} stuck ingest`}
                 accent={(ov?.reliability.failed_actions_24h ?? 0) > 0 ? '#dc2626' : undefined} />
            <Kpi icon={<Activity size={15} />} label="Audit events (30d)" value={(dash?.activity.audit_events ?? 0).toLocaleString()}
                 foot="Hash-chained, tamper-evident" />
          </div>

          {/* ── Two-column: Users + Reliability ── */}
          <div style={S.twoCol}>
            {/* Users */}
            <section style={S.card}>
              <SectionHead icon={<Users size={15} />} title="Users" />
              <div style={S.miniStats}>
                <MiniStat label="Active" value={ov?.users.by_status['active'] ?? 0} tone="ok" />
                <MiniStat label="Locked" value={ov?.users.locked ?? 0} tone={ov?.users.locked ? 'warn' : 'mute'} />
                <MiniStat label="New (30d)" value={ov?.users.new_last_30d ?? 0} tone="mute" />
              </div>
              <div style={S.subhead}>Recent sign-ins</div>
              {ov?.users.recent_logins.length ? (
                <ul style={S.list}>
                  {ov.users.recent_logins.slice(0, 6).map((u, i) => (
                    <li key={i} style={S.listRow}>
                      <span style={S.listMain}>{u.name || u.email}</span>
                      <span style={S.listMeta}>{u.pharmacy || '—'} · {u.last_login_at ? timeAgo(u.last_login_at) : ''}</span>
                    </li>
                  ))}
                </ul>
              ) : <Empty>No sign-ins recorded yet.</Empty>}
            </section>

            {/* Reliability / errors */}
            <section style={S.card}>
              <SectionHead icon={<AlertTriangle size={15} />} title="Reliability" />
              <div style={S.miniStats}>
                <MiniStat label="Failed (24h)" value={ov?.reliability.failed_actions_24h ?? 0} tone={(ov?.reliability.failed_actions_24h ?? 0) > 0 ? 'bad' : 'ok'} />
                <MiniStat label="Stuck ingest" value={stuck} tone={stuck ? 'bad' : 'ok'} />
                <MiniStat label="Failed logins" value={ov?.reliability.accounts_with_failed_logins ?? 0} tone={(ov?.reliability.accounts_with_failed_logins ?? 0) > 0 ? 'warn' : 'mute'} />
              </div>
              <div style={S.subhead}>Recent failures</div>
              {ov?.reliability.recent_failures.length ? (
                <ul style={S.list}>
                  {ov.reliability.recent_failures.slice(0, 6).map((f, i) => (
                    <li key={i} style={S.listRow}>
                      <code style={S.failAction}>{f.action}</code>
                      <span style={S.listMeta}>{f.actor_role} · {timeAgo(f.occurred_at)}</span>
                    </li>
                  ))}
                </ul>
              ) : <Empty>No failures in the last 24 hours.</Empty>}

              {/* App Insights exceptions — real exception tracking */}
              {ov?.app_errors?.available && (
                <>
                  <div style={{ ...S.subhead, marginTop: 16 }}>
                    App exceptions (24h){typeof ov.app_errors.failed_requests_5xx === 'number' ? ` · ${ov.app_errors.failed_requests_5xx} server errors` : ''}
                  </div>
                  {ov.app_errors.top_exceptions && ov.app_errors.top_exceptions.length > 0 ? (
                    <ul style={S.list}>
                      {ov.app_errors.top_exceptions.slice(0, 5).map((e, i) => (
                        <li key={i} style={S.listRow}>
                          <span style={S.exType} title={e.message}>{e.type}</span>
                          <span style={S.exCount}>{e.count}×</span>
                        </li>
                      ))}
                    </ul>
                  ) : <Empty>No exceptions captured in the last 24 hours.</Empty>}
                </>
              )}
              {ov?.app_errors && !ov.app_errors.available && (
                <div style={{ ...S.note, marginTop: 12 }}>
                  Live exception tracking unavailable{ov.app_errors.reason ? ` — ${ov.app_errors.reason}` : ''}. Needs Log Analytics Reader on the app identity.
                </div>
              )}
              <p style={S.note}>{ov?.reliability.note}</p>
            </section>
          </div>

          {/* ── Cost by customer (compact) ── */}
          {dash?.per_customer_cost && dash.per_customer_cost.customers.length > 0 && (
            <section style={S.card}>
              <SectionHead icon={<DollarSign size={15} />} title="Cost by customer · month to date"
                           right={<a href="/admin/operations" style={S.deepLink}>Full breakdown <ArrowRight size={12} /></a>} />
              <div style={S.barList}>
                {(() => {
                  const max = Math.max(...dash!.per_customer_cost!.customers.map(c => c.total_cost_usd), 1)
                  return dash!.per_customer_cost!.customers.slice(0, 6).map((c, i) => (
                    <div key={i} style={S.barRow}>
                      <span style={S.barLabel}>{c.name}</span>
                      <div style={S.barTrack}>
                        <div style={{ ...S.barFill, width: `${Math.max(2, (c.total_cost_usd / max) * 100)}%` }} />
                      </div>
                      <span style={S.barValue}>${c.total_cost_usd.toFixed(2)}</span>
                    </div>
                  ))
                })()}
              </div>
            </section>
          )}

          {/* ── Live ingest monitor ── */}
          <section style={S.card}>
            <SectionHead icon={<Database size={15} />} title="Ingest pipeline · live"
                         right={<span style={S.liveDot}><span style={S.pulse} />live</span>} />
            <div style={S.statusGroups}>
              <StatusCounts title="Parse" counts={dash?.ingest_queue.parse_status || {}} badKeys={['parse_error']} />
              <StatusCounts title="Mirror" counts={dash?.ingest_queue.mirror_status || {}} badKeys={['mirror_error']} />
            </div>
          </section>
        </>
      )}
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}@keyframes pulse{0%,100%{opacity:1}50%{opacity:.3}}`}</style>
    </div>
  )
}

/* ── helpers ── */

/* ── components ── */
function ArchNode({ icon, label, sub, state }: { icon: React.ReactNode; label: string; sub: string; state: 'ok' | 'warn' }) {
  return (
    <div style={S.archNode}>
      <div style={{ ...S.archIcon, borderColor: state === 'ok' ? '#0e7490' : '#b45309', color: state === 'ok' ? '#0e7490' : '#b45309' }}>{icon}</div>
      <div style={S.archLabel}>{label}</div>
      <div style={S.archSub}>{sub}</div>
    </div>
  )
}
function ArchArrow() { return <div style={S.archArrow}>→</div> }
function ArchPipe({ label, value, bad, muted }: { label: string; value: string; bad: boolean; muted?: boolean }) {
  return (
    <div style={S.pipe}>
      <span style={S.pipeLabel}>{label}</span>
      <span style={{ ...S.pipeValue, color: bad ? '#dc2626' : muted ? '#64748b' : '#0e7490' }}>
        {bad ? <CircleAlert size={12} /> : <CircleCheck size={12} />}{value}
      </span>
    </div>
  )
}
function Kpi({ icon, label, value, foot, accent }: { icon: React.ReactNode; label: string; value: React.ReactNode; foot: string; accent?: string }) {
  return (
    <div style={S.kpi}>
      <div style={S.kpiTop}>{icon}<span style={S.kpiLabel}>{label}</span></div>
      <div style={{ ...S.kpiValue, color: accent || '#0f172a' }}>{value}</div>
      <div style={S.kpiFoot}>{foot}</div>
    </div>
  )
}
function SectionHead({ icon, title, right }: { icon: React.ReactNode; title: string; right?: React.ReactNode }) {
  return <div style={S.sectionHead}><span style={S.sectionTitle}>{icon}{title}</span>{right}</div>
}
function MiniStat({ label, value, tone }: { label: string; value: number; tone: 'ok' | 'warn' | 'bad' | 'mute' }) {
  const c = tone === 'ok' ? '#059669' : tone === 'warn' ? '#b45309' : tone === 'bad' ? '#dc2626' : '#475569'
  return <div style={S.miniStat}><div style={{ ...S.miniValue, color: c }}>{value}</div><div style={S.miniLabel}>{label}</div></div>
}
function StatusCounts({ title, counts, badKeys }: { title: string; counts: Record<string, number>; badKeys: string[] }) {
  const keys = Object.keys(counts)
  return (
    <div style={S.statusGroup}>
      <div style={S.statusGroupTitle}>{title}</div>
      {keys.length === 0 ? <Empty>No events</Empty> : keys.map(k => {
        const bad = badKeys.includes(k) && counts[k] > 0
        return <div key={k} style={S.statusLine}><span style={{ color: bad ? '#dc2626' : '#334155', fontWeight: bad ? 700 : 400 }}>{k}</span><span style={{ fontWeight: 600, color: bad ? '#dc2626' : '#0f172a' }}>{counts[k]}</span></div>
      })}
    </div>
  )
}
function Empty({ children }: { children: React.ReactNode }) { return <div style={S.empty}>{children}</div> }

/* ── styles: slate + cyan operations console ── */
const S: Record<string, React.CSSProperties> = {
  page: { maxWidth: '100%', margin: '0 auto' },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 20 },
  eyebrow: { fontSize: 11, letterSpacing: 1.5, fontWeight: 700, color: '#0e7490' },
  h1: { fontSize: 28, fontWeight: 800, margin: '2px 0 0', color: '#0f172a', letterSpacing: -0.5 },
  headRight: { display: 'flex', alignItems: 'center', gap: 12 },
  updated: { fontSize: 12, color: '#64748b' },
  refreshBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, padding: '7px 12px', border: '1px solid #cbd5e1', borderRadius: 8, background: '#fff', color: '#334155', cursor: 'pointer' },
  errorBanner: { display: 'flex', alignItems: 'center', gap: 8, background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: 12, borderRadius: 10, marginBottom: 16, fontSize: 13 },
  loading: { display: 'flex', alignItems: 'center', gap: 10, padding: 48, justifyContent: 'center', color: '#64748b' },
  card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: 20, marginBottom: 16, boxShadow: '0 1px 2px rgba(15,23,42,0.04)' },
  heroCard: { background: 'linear-gradient(180deg,#f8fafc 0%,#fff 60%)' },
  heroStatus: { display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 },
  statusDot: { width: 14, height: 14, borderRadius: '50%', boxShadow: '0 0 0 4px rgba(16,185,129,0.15)' },
  statusLabel: { fontSize: 17, fontWeight: 700, color: '#0f172a' },
  statusSub: { fontSize: 13, color: '#64748b', marginTop: 1 },
  arch: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 4, flexWrap: 'wrap' },
  archNode: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flex: 1, minWidth: 72 },
  archIcon: { width: 40, height: 40, borderRadius: 10, border: '1.5px solid', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff' },
  archLabel: { fontSize: 12, fontWeight: 700, color: '#1e293b' },
  archSub: { fontSize: 10.5, color: '#94a3b8', textAlign: 'center' },
  archArrow: { color: '#cbd5e1', fontSize: 18, alignSelf: 'center', paddingTop: 6 },
  archFoot: { display: 'flex', gap: 20, marginTop: 18, paddingTop: 16, borderTop: '1px solid #f1f5f9', flexWrap: 'wrap' },
  pipe: { display: 'flex', flexDirection: 'column', gap: 2 },
  pipeLabel: { fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 },
  pipeValue: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, fontWeight: 600 },
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))', gap: 12, marginBottom: 16 },
  kpi: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 },
  kpiTop: { display: 'flex', alignItems: 'center', gap: 6, color: '#64748b' },
  kpiLabel: { fontSize: 12, fontWeight: 600 },
  kpiValue: { fontSize: 30, fontWeight: 800, margin: '6px 0 2px', letterSpacing: -1 },
  kpiFoot: { fontSize: 11.5, color: '#94a3b8' },
  twoCol: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))', gap: 16 },
  sectionHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 },
  sectionTitle: { display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 14, fontWeight: 700, color: '#0f172a' },
  deepLink: { display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 12, color: '#0891b2', textDecoration: 'none', fontWeight: 600 },
  miniStats: { display: 'flex', gap: 18, marginBottom: 16 },
  miniStat: { flex: 1 },
  miniValue: { fontSize: 22, fontWeight: 800 },
  miniLabel: { fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4 },
  subhead: { fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 },
  list: { listStyle: 'none', margin: 0, padding: 0 },
  listRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f8fafc', gap: 12 },
  listMain: { fontSize: 13, color: '#1e293b', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  listMeta: { fontSize: 11.5, color: '#94a3b8', whiteSpace: 'nowrap' },
  failAction: { fontSize: 12, color: '#b91c1c', background: '#fef2f2', padding: '1px 6px', borderRadius: 4, fontFamily: 'ui-monospace,monospace' },
  exType: { fontSize: 12.5, color: '#7c2d12', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  exCount: { fontSize: 12, fontWeight: 700, color: '#b91c1c', whiteSpace: 'nowrap' },
  note: { fontSize: 11, color: '#94a3b8', marginTop: 12, lineHeight: 1.5 },
  barList: { display: 'flex', flexDirection: 'column', gap: 10 },
  barRow: { display: 'flex', alignItems: 'center', gap: 12 },
  barLabel: { fontSize: 12.5, color: '#334155', width: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  barTrack: { flex: 1, height: 8, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' },
  barFill: { height: '100%', background: 'linear-gradient(90deg,#0891b2,#06b6d4)', borderRadius: 4 },
  barValue: { fontSize: 13, fontWeight: 700, color: '#0f172a', width: 64, textAlign: 'right' },
  liveDot: { display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, color: '#059669', textTransform: 'uppercase', letterSpacing: 0.5 },
  pulse: { width: 7, height: 7, borderRadius: '50%', background: '#10b981', animation: 'pulse 1.8s ease-in-out infinite' },
  statusGroups: { display: 'flex', gap: 32, flexWrap: 'wrap' },
  statusGroup: { minWidth: 160 },
  statusGroupTitle: { fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6 },
  statusLine: { display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0' },
  empty: { fontSize: 12.5, color: '#94a3b8', padding: '4px 0' },
}
